/**
 * SingleViewLobby Component - Optimized single-section view
 * Only renders the active section for maximum performance
 */

import React, { useMemo, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';

import Section from '../Section';
import { FavoritesSection, useFavoritesStore } from '../../features/favorites';

import { useSections, useAppStore } from '../../store/app/appStore';
import { useOpenType } from '../../core/providers/OpenTypeContext';
import { useIsPortrait } from '../../shared/hooks/useIsPortrait';
import { useSettleScrollOnRotate } from '../../shared/hooks/useSettleScrollOnRotate';

import { FAVORITE_SECTION_NAME, LOBBY_SECTION_NAME, SECTION_NAMES } from '@repo/common';

import styles from './Lobby.module.css';
import { useCampaignSectionGate } from 'features/campaigns';
import { CampaignSingleViewSection } from 'features/campaigns/_exports/ui';
import { SingleViewTopWins } from '../../features/topWins';
import LoadingIndicator from '../../shared/components/LoadingIndicator';

interface SingleViewLobbyProps {
  showTournamentNavigation?: boolean;
  hidden?: boolean;
  campaingOnlyView?: boolean;
}

const SingleViewLobby: React.FC<SingleViewLobbyProps> = ({ showTournamentNavigation, hidden, campaingOnlyView }) => {
  const { sections, selectedSection } = useSections();
  const setSelectedSection = useAppStore((state) => state.setSelectedSection);
  const { openType } = useOpenType();
  const { favorites } = useFavoritesStore();
  const isPortrait = useIsPortrait();
  const lobbyContainerRef = useRef<HTMLDivElement>(null);
  useSettleScrollOnRotate(lobbyContainerRef);
  const { isCampaignEnabled } = useCampaignSectionGate();
  const { t } = useTranslation();

  // Find the active section - memoized to prevent recalculation
  const activeSection = useMemo(() => {
    if (selectedSection === FAVORITE_SECTION_NAME) {
      return null; // Favorites will be rendered separately
    }
    return sections.find(s => s.name === selectedSection);
  }, [sections, selectedSection]);

  // Fallback mechanism: If we're in single-view but the selected section doesn't exist,
  // switch to multi-view to provide a better user experience
  const setViewMode = useAppStore((state) => state.setViewMode);

  useEffect(() => {
    // LOBBY is the virtual multi-view home — it never exists in sections[], so skipping it
    // prevents React Strict Mode's effect double-invoke from undoing a pending openSection navigation.
    // If the selected section is a campaign section, fall back to multi-view.
    if (sections.length > 0 && !activeSection && selectedSection !== FAVORITE_SECTION_NAME && selectedSection !== LOBBY_SECTION_NAME && isCampaignEnabled(selectedSection) !== true) {
      // Section not found in single-view mode - fallback to multi-view
      // This can happen in various scenarios (e.g., returning from game, section removed, etc.)
      setViewMode('multi');
      setSelectedSection(LOBBY_SECTION_NAME);
    }
  }, [sections, activeSection, selectedSection, setSelectedSection, setViewMode, isCampaignEnabled]);

  // Show loading indicator while sections are loading
  if (sections.length === 0 && selectedSection !== FAVORITE_SECTION_NAME) {
    return <LoadingIndicator show />;
  }

  // Only render the active section - huge performance win!
  return (
    <div 
      ref={lobbyContainerRef}
      className={styles.lobbyContainer} 
      data-view="single" 
      data-section-container
      data-open-type={openType}
      data-section={selectedSection}
      data-tournament-navigation={showTournamentNavigation || false}
      data-campaign-only={campaingOnlyView || false}
      style={hidden ? { display: 'none' } : undefined}
    >
      {selectedSection === FAVORITE_SECTION_NAME ? (
        <FavoritesSection
          data={favorites}
          isActive={true}
          isPortrait={isPortrait}
          lobbyContainerRef={lobbyContainerRef}
        />
      ) : isCampaignEnabled(selectedSection) === true ? (
        <CampaignSingleViewSection sectionName={selectedSection} />
      ) : selectedSection === SECTION_NAMES.TOP_WIN_GAMES ? (
        <SingleViewTopWins />
      ) : activeSection ? (
        <Section 
          data={activeSection}
          isActive={true}
          isPortrait={isPortrait}
          lobbyContainerRef={lobbyContainerRef}
        />
      ) : (
        <div className={styles.emptyLobby}>
          {t('section_not_found', 'Section not found')}
        </div>
      )}
    </div>
  );
};

// This component only rerenders when one of the following props changes
export default memo(SingleViewLobby, (prevProps, nextProps) => {
  return (
    prevProps.showTournamentNavigation === nextProps.showTournamentNavigation &&
    prevProps.hidden === nextProps.hidden &&
    prevProps.campaingOnlyView === nextProps.campaingOnlyView
  );
});